import axios from "axios";
import type { AxiosResponse, Method } from "axios";
import { hideLoader, showLoader } from "../components/common/Loader";
import { callToast } from "../components/common/Toast";
import { getIdToken } from "../utils/utils";

interface ApiOptions {
  customHeaders?: Record<string, string>;
  responseType?: "blob" | "json";
  showGlobalLoader?: boolean;
  notifyOnError?: boolean;
  signal?: AbortSignal;
}

type NotifiedApiError = Error & {
  __reconErrorNotified?: boolean;
};

const notifyUnauthorized = () => {
  window.dispatchEvent(new Event("app:unauthorized"));
};

const extractServerMessage = (data: unknown): string | null => {
  if (!data) {
    return null;
  }

  if (typeof data === "string") {
    return data.trim() || null;
  }

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return null;
  }

  if (typeof data === "object") {
    const candidates = ["message", "error", "errorMessage", "detail", "details", "title"] as const;

    for (const key of candidates) {
      const value = (data as Record<string, unknown>)[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }

  return null;
};

export const isTimeoutApiError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  const responseMessage = extractServerMessage(error.response?.data)?.toLowerCase() ?? "";
  const axiosMessage = error.message?.toLowerCase() ?? "";

  return status === 504 || responseMessage.includes("timed out") || axiosMessage.includes("timeout");
};

export const isCanceledApiError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  return error.code === "ERR_CANCELED" || error.name === "CanceledError";
};

export const getApiErrorMessage = (error: unknown, fallbackMessage = "Your request failed. Please try again.") => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const responseMessage = extractServerMessage(error.response?.data);

    if (isTimeoutApiError(error)) {
      return fallbackMessage !== "Your request failed. Please try again."
        ? fallbackMessage
        : "The request timed out before the server could finish. Try fewer networks or a smaller date range.";
    }

    if (responseMessage) {
      return responseMessage;
    }

    if (!error.response) {
      return "We couldn't reach the server. Please check your connection and try again.";
    }

    if (status === 401) {
      return "Your session has expired. Please sign in again.";
    }

    if (status === 403) {
      return "You do not have permission to perform this action.";
    }

    if (status === 404) {
      return "The requested resource could not be found.";
    }

    if (status && status >= 500) {
      return "The server could not complete your request. Please check your parameters.";
    }

    if (status && status >= 400) {
      return "Your request could not be completed. Please review the details and try again.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

export const notifyApiError = (error: unknown, fallbackMessage?: string) => {
  const typedError = error as NotifiedApiError | undefined;

  if (typedError?.__reconErrorNotified) {
    return;
  }

  if (typedError) {
    typedError.__reconErrorNotified = true;
  }

  callToast("error", getApiErrorMessage(error, fallbackMessage), {
    autoHideDuration: isTimeoutApiError(error) ? 12000 : 3000,
  });
};

const request = async <TResponse>(
  method: Method,
  endpoint: string,
  data: unknown = null,
  options: ApiOptions = {},
): Promise<AxiosResponse<TResponse>> => {
  const idToken = getIdToken();

  if (!idToken) {
    notifyUnauthorized();
    const authError = new Error("Your session has expired. Please sign in again.") as NotifiedApiError;
    authError.__reconErrorNotified = true;
    callToast("error", authError.message);
    throw authError;
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: idToken,
    ...options.customHeaders,
  };

  if (options.showGlobalLoader !== false) {
    showLoader();
  }

  try {
    return await axios<TResponse>({
      method,
      url: `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
      data,
      headers,
      responseType: options.responseType ?? "json",
      signal: options.signal,
    });
  } catch (error) {
    if (isCanceledApiError(error)) {
      throw error;
    }

    if (axios.isAxiosError(error) && error.response?.status === 401) {
      notifyUnauthorized();
    }

    if (options.notifyOnError !== false) {
      notifyApiError(error);
    }

    throw error;
  } finally {
    if (options.showGlobalLoader !== false) {
      hideLoader();
    }
  }
};

// Legacy call sites depend on the default response shape until they are typed individually.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const api = async <TResponse = any>(
  method: Method,
  endpoint: string,
  data: unknown = null,
  options: ApiOptions = {},
): Promise<TResponse> => {
  const response = await request<TResponse>(method, endpoint, data, options);
  return response.data;
};

// Use when callers need both payload and HTTP status code.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const apiWithStatus = async <TResponse = any>(
  method: Method,
  endpoint: string,
  data: unknown = null,
  options: ApiOptions = {},
): Promise<{ data: TResponse; status: number }> => {
  const response = await request<TResponse>(method, endpoint, data, options);
  return { data: response.data, status: response.status };
};

export default api;
